<template>
  <div class="flex flex-col">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <p class="text-sm text-gray-600 flex-1 min-w-0">
        Manage URA integration consumer credentials used by the integration service.
      </p>
      <div class="flex flex-wrap items-center gap-2 shrink-0">
        <button type="button" class="btn" :disabled="loading" @click="loadKeys">
          <i class="fa-solid fa-rotate-right" :class="{ 'fa-spin': loading }"></i>
          <span>Refresh</span>
        </button>
        <button
          v-if="loaded && keysConfigured"
          type="button"
          class="btn-sm-danger"
          :disabled="deleting || loading || saving"
          @click="confirmDelete"
        >
          <i v-if="deleting" class="fa fa-spinner fa-spin"></i>
          <i v-else class="fa-solid fa-trash"></i>
          <span>Delete keys</span>
        </button>
      </div>
    </div>

    <div class="relative min-h-[280px]">
      <div
        v-if="loading && !loaded"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded border border-gray-200 bg-white/90"
      >
        <i class="fa-solid fa-circle-notch fa-spin text-3xl text-primary"></i>
        <p class="mt-3 text-sm text-gray-500">Loading URA integration keys...</p>
      </div>

      <div :class="{ invisible: loading && !loaded }">
        <div class="mb-4">
          <span
            class="px-2 py-0.5 rounded-full text-xs font-semibold"
            :class="keysConfigured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'"
          >
            {{ keysConfigured ? "Keys configured" : "No keys configured" }}
          </span>
        </div>

        <form class="max-w-lg space-y-4" @submit.prevent="saveKeys">
          <div>
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Consumer key</label>
            <input
              v-model="form.consumerKey"
              type="text"
              autocomplete="off"
              placeholder="Enter consumer key"
              class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50 mt-1"
              :disabled="loading || saving"
              required
            />
          </div>
          <div>
            <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Consumer secret</label>
            <div class="relative mt-1">
              <input
                v-model="form.consumerSecret"
                :type="showSecret ? 'text' : 'password'"
                autocomplete="new-password"
                placeholder="Enter consumer secret"
                class="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-gray-50"
                :disabled="loading || saving"
                required
              />
              <button
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-primary"
                :title="showSecret ? 'Hide secret' : 'Show secret'"
                @click="showSecret = !showSecret"
              >
                <i :class="showSecret ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
              </button>
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="submit"
              class="btn-primary"
              :disabled="loading || saving || deleting || !allFieldsEdited"
            >
              <i v-if="saving" class="fa fa-spinner fa-spin"></i>
              <i v-else class="fa-solid fa-save"></i>
              <span>{{ keysConfigured ? "Update keys" : "Save keys" }}</span>
            </button>
            <span v-if="!allFieldsEdited" class="text-xs text-gray-500">
              Edit both the consumer key and consumer secret to save.
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useConfirmDialog, useToast } from "@/composables";
import {
  deleteUraIntegrationKeys,
  extractErrorMessage,
  fetchUraIntegrationKeys,
  saveUraIntegrationKeys,
} from "./uraApi";
import { useAllFieldsEdited } from "./useAllFieldsEdited";

const { successToast, errorToast, warningToast } = useToast();
const { showConfirm } = useConfirmDialog();

const loading = ref(false);
const saving = ref(false);
const deleting = ref(false);
const loaded = ref(false);
const keysConfigured = ref(false);
const showSecret = ref(false);
const form = ref({
  consumerKey: "",
  consumerSecret: "",
});
const { allFieldsEdited, markPristine } = useAllFieldsEdited(form);

const applyKeysToForm = (consumerKey: string | null, consumerSecret: string | null, configured: boolean) => {
  keysConfigured.value = configured;
  form.value.consumerKey = consumerKey || "";
  form.value.consumerSecret = consumerSecret || "";
  markPristine();
};

const loadKeys = async () => {
  loading.value = true;
  try {
    const keys = await fetchUraIntegrationKeys();
    applyKeysToForm(keys.consumerKey, keys.consumerSecret, keys.configured);
  } catch (error: unknown) {
    const err = error as { response?: { status?: number } };
    if (err.response?.status === 404) {
      applyKeysToForm(null, null, false);
    } else {
      errorToast("Error", extractErrorMessage(error, "Could not load URA integration keys"));
    }
  } finally {
    loading.value = false;
    loaded.value = true;
  }
};

const saveKeys = async () => {
  if (!allFieldsEdited.value) {
    warningToast("Validation", "Edit both the consumer key and consumer secret before saving");
    return;
  }
  const consumerKey = form.value.consumerKey.trim();
  const consumerSecret = form.value.consumerSecret.trim();
  saving.value = true;
  try {
    await saveUraIntegrationKeys({ consumerKey, consumerSecret });
    successToast("Success", "URA integration keys saved");
    await loadKeys();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not save URA integration keys"));
  } finally {
    saving.value = false;
  }
};

const confirmDelete = async () => {
  const confirmed = await showConfirm(
    "Delete URA integration keys? This will remove the configured credentials.",
    {
      title: "Delete URA Keys",
      variant: "danger",
      okTitle: "Delete",
      cancelTitle: "Cancel",
    }
  );
  if (!confirmed) return;

  deleting.value = true;
  try {
    await deleteUraIntegrationKeys();
    successToast("Success", "URA integration keys deleted");
    applyKeysToForm(null, null, false);
    showSecret.value = false;
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not delete URA integration keys"));
  } finally {
    deleting.value = false;
  }
};

onMounted(() => {
  loadKeys();
});

defineExpose({ refresh: loadKeys });
</script>
